import * as React from 'react';
import { useContext } from 'react';

import type {
  AuthResult,
  HumanStatusResponse,
  LogInCredentials,
  LogInResponse,
  PasswordlessTokenLoginParams,
  PasswordlessTokenLoginResponse,
  RegisterParams,
  RegisterResponse,
} from './sessionClient';
import type { User } from './User';

export type Authenticating = {
  state: 'authenticating';
  user: undefined;
};
export type NotAuthenticated = {
  state: 'not_authenticated';
  user: null;
};
export type Authenticated = {
  state: 'authenticated';
  user: User;
};
export type AuthenticationError = {
  state: 'error';
  user: undefined;
  /** Message for developer. Do not show to the user. */
  message: string;
};

export type AuthenticationState =
  | Authenticating
  | NotAuthenticated
  | Authenticated
  | AuthenticationError;

export type EmailPasswordCredentials = {
  email: string;
  /** Password in plain text. Yes, in plain text :( */
  password: string;
};
export type GoogleCredentials = {
  googleIdToken: string;
};
export type FacebookCredentials = {
  facebookIdToken: string;
};

export type LoginCredentials = EmailPasswordCredentials | GoogleCredentials | FacebookCredentials;
export type LoginSuccess = { success: true };
export type LoginFailure = { success: false; displayErrorMessage: string };
export type LoginResult = LoginSuccess | LoginFailure;
export type ValidateResetPasswordTokenSuccess = { success: true };
export type ValidateResetPasswordTokenFailure = { success: false; displayErrorMessage?: string };
export type ValidateResetPasswordTokenResult =
  | ValidateResetPasswordTokenSuccess
  | ValidateResetPasswordTokenFailure;
export type ResetPasswordSuccess = { success: true };
export type ResetPasswordFailure = { success: false; displayErrorMessage?: string };
export type ResetPasswordResult = ResetPasswordSuccess | ResetPasswordFailure;
export const DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE = 'Sorry, something went wrong.';

export interface AuthContextState {
  authentication: AuthenticationState;
  /**
   * Logs the user out. The window will be refreshed.
   */
  logout: () => void;
  /**
   * Function to refresh the authentication state
   * The existing authentication state will remain the same until the refresh
   * finishes (i.e. the network calls complete again).
   * It's useful if a change in logged-in state is made that does not cause a
   * reload of this library (login/logout via. API, third party login, etc.)
   */
  refresh: () => void;
  /**
   * Attempts to verify login with the given credentials.
   * Note the user must be logged in to use this function.
   *
   * This can be called to refresh a session's last_login_at timestamp
   * to allow the user to perform more sensitive actions.
   *
   * See more documentation at:
   * https://opendoor.atlassian.net/wiki/spaces/SELL/pages/1917845556/Last+Login+Authorization+Feature
   */
  verifyLogin: (credentials: LoginCredentials) => Promise<LoginResult>;

  validateResetPasswordToken: (
    resetPasswordToken: string,
  ) => Promise<ValidateResetPasswordTokenResult>;

  resetPassword: (token: string, password: string) => Promise<ResetPasswordResult>;

  /** Signs the user in against Rails `POST /login`; `data.redirectTo` carries the server's post-login destination. */
  logIn: (credentials: LogInCredentials) => Promise<AuthResult<LogInResponse>>;

  /**
   * Creates an account against Rails `POST /register`. Pass
   * `{ hasGeneratedPassword: true }` for the passwordless variant (Rails
   * generates the password and mails a setup link). `data.redirectTo` carries
   * the server's post-register destination.
   */
  register: (params: RegisterParams) => Promise<AuthResult<RegisterResponse>>;

  /**
   * Redeems a one-time passwordless token (login-lite / mobile-handoff link)
   * against Rails `POST /register`, logging the human in. `data.redirectTo`
   * carries the server's post-login destination.
   */
  loginWithPasswordlessToken: (
    params: PasswordlessTokenLoginParams,
  ) => Promise<AuthResult<PasswordlessTokenLoginResponse>>;

  /** Looks an email up against `GET /humans/status`; surfaces `isNewHuman`/`hasGeneratedPassword` for step routing. */
  requestHumanStatus: (email: string) => Promise<AuthResult<HumanStatusResponse>>;

  /**
   * Human-status-guarded reset: looks the email up, then only asks Rails to
   * send a reset/setup email for an existing account. A new email resolves
   * with `isNewHuman: true` and no email sent.
   */
  requestPasswordReset: (email: string) => Promise<AuthResult<HumanStatusResponse>>;
}

const defaultWritePathResult: AuthResult<never> = {
  success: false,
  error: { code: 'unknown', message: '' },
};

export const AuthContext = React.createContext<AuthContextState>({
  authentication: {
    state: 'not_authenticated',
    user: null,
  },
  logout: () => {},
  refresh: () => {},
  verifyLogin: async () => ({
    success: false,
    displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  }),
  validateResetPasswordToken: async () => ({ success: true }),
  resetPassword: async () => ({ success: true }),
  logIn: async () => defaultWritePathResult,
  register: async () => defaultWritePathResult,
  loginWithPasswordlessToken: async () => defaultWritePathResult,
  requestHumanStatus: async () => defaultWritePathResult,
  requestPasswordReset: async () => defaultWritePathResult,
});

export interface UseAuth extends AuthContextState {
  /**
   * Convenience field for accessing `authentication.user`.
   * Returns User if authentication.state is `authenticated`.
   * Returns null if authentication.state is `not_authenticated`.
   * Returns undefined if authentication.state is `authenticating` or `error`.
   */
  user: User | null | undefined;
}

/** Gets AuthenticationState. */
export function useAuth(): UseAuth {
  const authContext = useContext(AuthContext);
  return {
    ...authContext,
    user: authContext.authentication.user,
  };
}

export interface UseAuthAuthenticated extends UseAuth {
  authentication: Authenticated;
  user: User;
}

/**
 * Gets AuthenticationState, but only if used in an authenticated context.
 *
 * This hook will guarantee that a user exists, but throws an error if not in an authenticated context.
 *
 * ```typescript
 * const Hello = () => {
 *   const { authentication } = useAuth();
 *   if (authentication.state === 'authenticated') {
 *     return <HelloAuthenticated />;
 *   } else {
 *     return <HelloMaybeAuthenticated />;
 *   }
 * };
 *
 * const HelloAuthenticated = () => {
 *   // GOOD. This will get the user and we don't have to check for null/undefined.
 *   const { user } = useAuthAuthenticated();
 *   return `Hello, ${user.name}`;
 * };
 *
 * const HelloMaybeAuthenticated = () => {
 *   // BAD. This will crash.
 *   const { user } = useAuthAuthenticated();
 *
 *   // GOOD. This will get the user but we must check for null/undefined before using it.
 *   const { user } = useAuth();
 *   if (user) {
 *     return `Hello, ${user.name}`;
 *   } else {
 *     return 'Hello, stranger';
 *   }
 * };
 * ```
 * @throws an error if not in an authenticated context
 */
export function useAuthAuthenticated(): UseAuthAuthenticated {
  const auth = useAuth();
  const { authentication } = auth;

  if (authentication.state === 'authenticated') {
    return { ...auth, authentication, user: authentication.user };
  } else {
    throw new Error(
      'DEVELOPER ERROR: useAuthAuthenticated must be used in an authenticated context',
    );
  }
}
